// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import { formatNumber } from "@mcmc/App/Components/FormattedNumber";
import Tooltip from "@mcmc/MobiusOverrides/Tooltip";
import { screen } from "@mcmc/XDL/Helpers";
import CaretLeft from "@mcmc/XDL/Icons/CaretLeft";
import CaretRight from "@mcmc/XDL/Icons/CaretRight";
import { styledLinksMenu } from "@mcmc/XDL/Utilities";
import * as React from "react";
import { useState } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { Instance } from "tippy.js";

export interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the the URL of the next page in the series */
        nextPage?: string;
        /* Controls if we wanted to show the totalCount in the pagination or not  */
        totalCountLabelConfig?: TotalCountLabelConfig;
        /* if true it will only show the page numbers */
        isBottom?: boolean;
        /* if true it will hide the page size selector */
        hidePerPage?: boolean;
        /* if true it will show the page size selector only */
        perPageOnly?: boolean;
    }
>;

export interface TotalCountLabelConfig {
    showTotal: boolean;
    singularLabel?: string;
    pluralLabel?: string;
}

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    white-space: nowrap;
    flex: 1;
    ul {
        display: flex;
        align-items: center;
        list-style: none;
        margin: -5px;
        ${screen("lte", "xs")`
            flex-wrap: wrap;
            justify-content: center;
        `}
    }
    li {
        padding: 5px;
        display: flex;
    }

    ${injectCss}
`;

export const PerPageSelect = styled.span<InjectableCss & { showSeparator: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 10px 0;

    ${({ showSeparator }) => {
        if (showSeparator) {
            return css`
                border-bottom: 1px solid ${getColor("common.border")};
            `;
        }
        return null;
    }};

    & > ${/* sc-selector */ TypographyStyle} {
        margin-right: auto;
    }

    ${injectCss}
`;

export const PagesCountContainer = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    flex-direction: column;
                    & > ${/* sc-selector */ TypographyStyle}:not(:last-child) {
                        margin-bottom: 10px;
                    }
                `,
                null,
                null,
                null,
                null,
            ],
            "max",
        )};
`;

const totalCountStyles: TypographyPresentationProps = {
    variant: "h6",
    forwardAs: "span",
    css: css`
        margin-bottom: 0;
    `,
};

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        totalCountLabelConfig,
        isBottom,
        hidePerPage,
        perPageOnly,
        mergeCss,
        prevPage,
        nextPage,
        ...otherProps
    } = props;

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);
        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }

        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }

        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }

            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
        event.currentTarget.blur();
    };

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;
    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    let pagesToDisplay: ("e" | number)[] = [];

    if (finalPageIndex < 5) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 3) {
        pagesToDisplay = [1, 2, 3, "e", finalPageIndex];
    } else if (currentPage >= 3 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = [1, "e", finalPageIndex - 2, finalPageIndex - 1, finalPageIndex];
    }

    const [tooltipInstance, setTooltipInstance] = useState<Instance | null>(null);

    const paginationButtons = pagesToDisplay.map(page => {
        if (page === "e") {
            const tooltipPages: any = [];
            for (let pageIndex = 1; pageIndex <= finalPageIndex; pageIndex += 1) {
                tooltipPages.push(
                    <Link
                        key={`page${pageIndex}`}
                        onClick={() => {
                            if (props.onChangePage) {
                                props.onChangePage?.(pageIndex);
                                tooltipInstance?.hide();
                            }
                        }}
                        disabled={currentPage === pageIndex}
                    >
                        {`${translate("Page")} ${formatNumber(pageIndex)}`}
                    </Link>,
                );
            }
            return (
                <li key={`page${page}`}>
                    <Tooltip
                        text={tooltipPages}
                        trigger="mouseenter focus"
                        onOpen={({ instance }: any) => setTooltipInstance(instance)}
                        placement="bottom-start"
                        triggerAltText={translate("All Pages")}
                        cssOverrides={{
                            tooltipContainer: css`
                                ${styledLinksMenu(120)}
                            `,
                        }}
                        triggerComponent={
                            <Typography
                                key={page}
                                css={applyStyledProp(["cssOverrides", "ellipsis"], resolvedMergeCss)}
                            >
                                ...
                            </Typography>
                        }
                    ></Tooltip>
                </li>
            );
        }
        return (
            <li key={`page${page}`}>
                <Button
                    {...buttonDisplayProps(props, {
                        moreCss: numericButtonStyles,
                        page,
                        cssOverrides,
                        buttonProps,
                        currentPageButtonVariant,
                    })}
                    type="button"
                >
                    {formatNumber(page)}
                </Button>
            </li>
        );
    });

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navButton = (
        pageIndex: number,
        buttonLabel: string,
        color: string,
        disabled: boolean,
        dataTestSelector?: string,
        iconSrc?: React.ComponentType,
    ) => (
        <Button
            {...buttonProps}
            color={color}
            css={css`
                ${buttonProps.css}
                padding: 0 10px;
                ${TypographyStyle} {
                    ${screen("lte", "xs")`display:none`}
                }
                ${ButtonIcon} {
                    width: 9px;
                    height: 9px;
                    display: none;
                    margin: 0;
                    ${screen("lte", "xs")`display:flex`}
                }
            `}
            aria-label={buttonLabel}
            typographyProps={{ ...buttonProps.typographyProps, weight: 700 }}
            icon={{ src: iconSrc, position: "left" }}
            onClick={() => onChangePage(pageIndex)}
            disabled={disabled}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
        >
            {buttonLabel}
        </Button>
    );

    const firstItem = (currentPage - 1) * resultsPerPage + 1;
    const lastItem = Math.min(currentPage * resultsPerPage, resultsCount);

    if (
        resultsCount <= Math.min(resultsPerPageOptions[0], resultsPerPage) &&
        !(totalCountLabelConfig.showTotal && !isBottom)
    ) {
        return null;
    }

    const perPageSelectElement = (
        <Select
            onChange={changeResultsPerPage}
            label={resultsPerPageLabel || translate("Show")}
            labelPosition="left"
            preventEllipsis={true}
            value={resultsPerPage}
            {...spreadProps("selectProps" as any)}
            data-test-selector="pagination_resultsPerPage"
        >
            {resultsPerPageOptions.map((n: string) => (
                <option key={n} value={n}>
                    {n}
                </option>
            ))}
        </Select>
    );

    const onePageTotalDisplay = (
        <Typography {...totalCountStyles}>
            {translate(
                `{0} ${
                    resultsCount > 1
                        ? totalCountLabelConfig.pluralLabel || "Items"
                        : totalCountLabelConfig.singularLabel || "Item"
                }`,
            ).replace("{0}", formatNumber(resultsCount))}
        </Typography>
    );

    if (perPageOnly) {
        if (resultsCount > resultsPerPageOptions[0]) {
            return perPageSelectElement;
        }
        return null;
    }

    return (
        <PaginationStyle
            css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)}
            {...omitSingle(otherProps, "cssOverrides")}
        >
            {!isBottom && !hidePerPage && (
                <PerPageSelect
                    css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)}
                    showSeparator={pagesToDisplay.length > 1}
                >
                    {pagesToDisplay.length === 1 && totalCountLabelConfig.showTotal && onePageTotalDisplay}
                    {resultsCount > resultsPerPageOptions[0] && perPageSelectElement}
                </PerPageSelect>
            )}
            {pagesToDisplay.length > 1 && (
                <PagesCountContainer>
                    {totalCountLabelConfig.showTotal && !isBottom && (
                        <Typography {...totalCountStyles}>
                            {translate(`${totalCountLabelConfig.pluralLabel || "Items"} {0} - {1} of {2}`)
                                .replace("{0}", formatNumber(firstItem))
                                .replace("{1}", formatNumber(lastItem))
                                .replace("{2}", formatNumber(resultsCount))}
                        </Typography>
                    )}

                    <LinkList
                        role="navigation"
                        aria-label={translate("Pagination navigation")}
                        css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                        data-test-selector={`pagination_currentPage_${currentPage}`}
                    >
                        <ul>
                            <li>
                                {navButton(
                                    currentPage - 1,
                                    translate("Prev"),
                                    "secondary",
                                    backDisabled,
                                    "pagination_previousButton",
                                    CaretLeft,
                                )}
                            </li>
                            {paginationButtons}
                            <li>
                                {navButton(
                                    currentPage + 1,
                                    translate("Next"),
                                    "primary",
                                    forwardDisabled,
                                    "pagination_nextButton",
                                    CaretRight,
                                )}
                            </li>
                        </ul>
                    </LinkList>
                </PagesCountContainer>
            )}
            {pagesToDisplay.length === 1 && totalCountLabelConfig.showTotal && hidePerPage && (
                <PagesCountContainer>{onePageTotalDisplay}</PagesCountContainer>
            )}
        </PaginationStyle>
    );
});

Pagination.defaultProps = {
    totalCountLabelConfig: {
        showTotal: false,
    },
    isBottom: false,
    hidePerPage: false,
    perPageOnly: false,
};

/** @component */
export default withTheme(Pagination) as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
